import { countryOptions } from './countries.js';

// Choices for the company profile (same codes as server/company.js) and
// how to display any profile value, for the form, the admin review and
// the public seller page.

export const PRIMARY_BUSINESS = ['manufacturer', 'wholesaler', 'distributor', 'exporter', 'importer', 'trader', 'service_provider', 'retailer', 'other'];
export const EMPLOYEES = ['1_10', '11_50', '51_200', '201_500', '501_1000', 'over_1000'];
export const SOCIAL = ['linkedin', 'facebook', 'instagram', 'youtube', 'x', 'tiktok', 'wechat'];
export const ADDRESS_KINDS = ['office', 'warehouse', 'factory', 'showroom', 'other'];
// Contact details the member can show publicly (server: PUBLIC_TOGGLES).
export const PUBLIC_TOGGLES = ['contact_person', 'designation', 'contact_email', 'phone', 'whatsapp', 'address', 'addresses', 'contacts'];
export const DOC_TYPES = ['registration', 'tax', 'import_export', 'iso', 'certificate', 'catalogue', 'other'];
export const ANNUAL_SALES = ['under_100k', '100k_500k', '500k_1m', '1m_5m', '5m_10m', '10m_50m', '50m_100m', 'over_100m'];
export const MARKETS = ['CA', 'US', 'MX', 'europe', 'asia', 'middle_east', 'africa', 'south_america', 'oceania'];
export const REGIONS = ['north_america', 'central_america', 'caribbean', 'south_america', 'europe', 'middle_east', 'africa', 'asia', 'oceania'];
export const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

// Form order; also the order of the admin "old vs new" review.
export const FIELD_ORDER = [
  'legal_name', 'business_number', 'description',
  'supplier_of', 'buyer_of', 'services', 'primary_business', 'year_established', 'annual_sales', 'certifications',
  'markets', 'domestic_markets', 'exported_to', 'imported_from',
  'bankers', 'brand_name', 'trade_name', 'division_name', 'brand_tagline',
  'contact_person', 'designation', 'phone', 'website',
  'address_line', 'city', 'region', 'postal_code', 'country',
  // Migration 025
  'description_html', 'employees', 'sectors', 'social', 'contact_email', 'whatsapp', 'addresses', 'contacts', 'public_fields',
];
// Form values that are lists / objects.
export const LIST_FIELDS = ['markets', 'domestic_markets', 'exported_to', 'imported_from', 'sectors', 'public_fields', 'addresses', 'contacts'];
export const OBJECT_FIELDS = ['social'];
export const PRIVATE_FIELDS = ['bankers', 'annual_sales', 'business_number', 'phone', 'address_line', 'postal_code'];

export const fieldLabel = (f, t) => t(`company.fields.${f}`);

let countryCache = {};
function countryName(code, lang) {
  countryCache[lang] ||= Object.fromEntries(countryOptions(lang).map((c) => [c.code, c.name]));
  return countryCache[lang][code] || code;
}

// Region slug (lowercase) or ISO country code (uppercase) → label.
export const placeLabel = (code, t, lang) => (/^[A-Z]{2}$/.test(code) ? countryName(code, lang) : t(`regions.${code}`));

const htmlText = (h) => String(h || '').replace(/<\/(p|li)>/g, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
// names: optional { categoryId: name } for "sectors".
export function displayValue(f, v, t, lang, profile = {}, names = {}) {
  if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length) || (f === 'social' && !Object.keys(v).length)) return '';
  switch (f) {
    case 'description_html': return htmlText(v);
    case 'employees': return t(`company.employees.${v}`);
    case 'sectors': return v.map((id) => names[id] || `#${id}`).join(', ');
    case 'social': return Object.entries(v).map(([k, u]) => `${t(`company.social.${k}`)}: ${u}`).join('\n');
    case 'addresses': return v.map((a) => `${t(`company.addressKinds.${a.kind}`)} — ${[a.line, a.city, a.region, a.postal, a.country].filter(Boolean).join(', ')}`).join('\n');
    case 'contacts': return v.map((c) => [c.name, c.title, c.email, c.phone].filter(Boolean).join(' · ')).join('\n');
    case 'public_fields': return v.map((k) => t(`contactPage.public.${k}`)).join(', ');
    case 'primary_business': return t(`company.business.${v}`);
    case 'annual_sales': return t(`company.sales.${v}`);
    case 'markets': case 'exported_to': case 'imported_from': return v.map((c) => placeLabel(c, t, lang)).join(', ');
    case 'domestic_markets': return v.map((c) => t(`provinces.${c}`)).join(', ');
    case 'country': return countryName(v, lang);
    case 'region': return profile.country === 'CA' && PROVINCES.includes(v) ? t(`provinces.${v}`) : v;
    default: return String(v);
  }
}

// Same check as the server (normalizeWebsite).
export function validWebsite(raw) {
  if (!raw) return true;
  try {
    const u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    return /^https?:$/.test(u.protocol) && /^[^.\s]+(\.[^.\s]+)+$/.test(u.hostname);
  } catch {
    return false;
  }
}
